import React, { useState, useMemo, useCallback } from 'react';
import './TableView.css';

const TableView = ({ 
  gedcomData, 
  onPersonSelect, 
  onSwitchToTree, 
  searchTerm = '', 
  filters = {} 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    birthDate: '',
    deathDate: '',
    birthPlace: '',
    deathPlace: ''
  });

  // Convert GEDCOM data to table format
  const tableData = useMemo(() => {
    if (!gedcomData || !gedcomData.individuals) return [];
    
    return Object.entries(gedcomData.individuals).map(([id, person]) => ({
      id,
      name: person.name || 'Unknown',
      birthDate: person.birth || '',
      deathDate: person.death || '',
      birthPlace: person.birthPlace || '',
      deathPlace: person.deathPlace || '',
      sex: person.sex || '',
      // Additional computed fields for better display
      lifespan: person.birth && person.death ? 
        `${person.birth} - ${person.death}` : 
        person.birth ? `${person.birth} - ` : 
        person.death ? ` - ${person.death}` : '',
      locations: [person.birthPlace, person.deathPlace]
        .filter(Boolean)
        .filter((place, index, arr) => arr.indexOf(place) === index)
        .join(', ')
    }));
  }, [gedcomData]);

  // Apply filters and search
  const filteredData = useMemo(() => {
    let filtered = tableData;

    // Apply global search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(person => 
        person.name.toLowerCase().includes(term) ||
        person.birthDate.toLowerCase().includes(term) ||
        person.deathDate.toLowerCase().includes(term) ||
        person.birthPlace.toLowerCase().includes(term) ||
        person.deathPlace.toLowerCase().includes(term)
      );
    }

    // Apply column-specific filters
    Object.entries(columnFilters).forEach(([column, filterValue]) => {
      if (filterValue) {
        const term = filterValue.toLowerCase();
        filtered = filtered.filter(person => 
          person[column].toLowerCase().includes(term)
        );
      }
    });

    // Apply external filters (from parent component)
    if (filters.gender && filters.gender !== 'all') {
      filtered = filtered.filter(person => person.sex === filters.gender);
    }

    if (filters.location && filters.location !== 'all') {
      filtered = filtered.filter(person => 
        person.birthPlace.includes(filters.location) ||
        person.deathPlace.includes(filters.location)
      );
    }

    if (filters.dateRange && filters.dateRange !== 'all') {
      const [startYear, endYear] = filters.dateRange.includes('-') 
        ? filters.dateRange.split('-').map(y => parseInt(y.replace('+', '')))
        : [parseInt(filters.dateRange.replace('+', '')), 9999];
      
      filtered = filtered.filter(person => {
        const birthYear = person.birthDate ? parseInt(person.birthDate.match(/\d{4}/)?.[0] || '0') : 0;
        const deathYear = person.deathDate ? parseInt(person.deathDate.match(/\d{4}/)?.[0] || '0') : 0;
        
        return (birthYear >= startYear && birthYear <= endYear) ||
               (deathYear >= startYear && deathYear <= endYear);
      });
    }

    return filtered;
  }, [tableData, searchTerm, columnFilters, filters]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      // Handle date sorting specially
      if (sortConfig.key.includes('Date')) {
        const aYear = parseInt(aValue.match(/\d{4}/)?.[0] || '0');
        const bYear = parseInt(bValue.match(/\d{4}/)?.[0] || '0');
        
        if (sortConfig.direction === 'asc') {
          return aYear - bYear;
        } else {
          return bYear - aYear;
        }
      }
      
      // String sorting
      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [filteredData, sortConfig]);

  // Handle column sorting
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Handle row selection
  const handleRowSelect = useCallback((personId, isSelected) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(personId);
      } else {
        newSet.delete(personId);
      }
      return newSet;
    });
  }, []);

  // Handle person details view
  const handlePersonClick = useCallback((person) => {
    if (onPersonSelect) {
      onPersonSelect(person.id);
    }
  }, [onPersonSelect]);

  // Handle switch to tree view
  const handleSwitchToTree = useCallback((personId) => {
    if (onSwitchToTree) {
      onSwitchToTree(personId);
    }
  }, [onSwitchToTree]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setColumnFilters({
      name: '',
      birthDate: '',
      deathDate: '',
      birthPlace: '',
      deathPlace: ''
    });
  }, []);

  // Get sort indicator
  const getSortIndicator = (column) => {
    if (sortConfig.key !== column) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="table-view">
      <div className="table-view-header">
        <div className="table-stats">
          <span className="total-count">
            {sortedData.length} of {tableData.length} people
          </span>
          {selectedRows.size > 0 && (
            <span className="selected-count">
              {selectedRows.size} selected
            </span>
          )}
        </div>
        
        <div className="table-actions">
          <button 
            className="clear-filters-btn"
            onClick={clearAllFilters}
            disabled={Object.values(columnFilters).every(v => !v)}
          >
            <i className="fas fa-times-circle"></i>
            Clear Filters
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="family-table">
          <thead>
            <tr>
              <th className="select-column">
                <input
                  type="checkbox"
                  checked={selectedRows.size === sortedData.length && sortedData.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRows(new Set(sortedData.map(p => p.id)));
                    } else {
                      setSelectedRows(new Set());
                    }
                  }}
                />
              </th>
              <th className="name-column sticky-column">
                <div className="column-header">
                  <button 
                    className="sort-button"
                    onClick={() => handleSort('name')}
                  >
                    Name {getSortIndicator('name')}
                  </button>
                  <input
                    type="text"
                    placeholder="Filter names..."
                    value={columnFilters.name}
                    onChange={(e) => setColumnFilters(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    className="column-filter"
                  />
                </div>
              </th>
              <th>
                <div className="column-header">
                  <button 
                    className="sort-button"
                    onClick={() => handleSort('birthDate')}
                  >
                    Birth Date {getSortIndicator('birthDate')}
                  </button>
                  <input
                    type="text"
                    placeholder="Filter birth dates..."
                    value={columnFilters.birthDate}
                    onChange={(e) => setColumnFilters(prev => ({
                      ...prev,
                      birthDate: e.target.value
                    }))}
                    className="column-filter"
                  />
                </div>
              </th>
              <th>
                <div className="column-header">
                  <button 
                    className="sort-button"
                    onClick={() => handleSort('deathDate')}
                  >
                    Death Date {getSortIndicator('deathDate')}
                  </button>
                  <input
                    type="text"
                    placeholder="Filter death dates..."
                    value={columnFilters.deathDate}
                    onChange={(e) => setColumnFilters(prev => ({
                      ...prev,
                      deathDate: e.target.value
                    }))}
                    className="column-filter"
                  />
                </div>
              </th>
              <th>
                <div className="column-header">
                  <button 
                    className="sort-button"
                    onClick={() => handleSort('birthPlace')}
                  >
                    Birth Place {getSortIndicator('birthPlace')}
                  </button>
                  <input
                    type="text"
                    placeholder="Filter birth places..."
                    value={columnFilters.birthPlace}
                    onChange={(e) => setColumnFilters(prev => ({
                      ...prev,
                      birthPlace: e.target.value
                    }))}
                    className="column-filter"
                  />
                </div>
              </th>
              <th>
                <div className="column-header">
                  <button 
                    className="sort-button"
                    onClick={() => handleSort('deathPlace')}
                  >
                    Death Place {getSortIndicator('deathPlace')}
                  </button>
                  <input
                    type="text"
                    placeholder="Filter death places..."
                    value={columnFilters.deathPlace}
                    onChange={(e) => setColumnFilters(prev => ({
                      ...prev,
                      deathPlace: e.target.value
                    }))}
                    className="column-filter"
                  />
                </div>
              </th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((person) => (
              <tr 
                key={person.id}
                className={`table-row ${selectedRows.has(person.id) ? 'selected' : ''}`}
              >
                <td className="select-cell">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(person.id)}
                    onChange={(e) => handleRowSelect(person.id, e.target.checked)}
                  />
                </td>
                <td className="name-cell sticky-column">
                  <div className="person-name-cell">
                    <div 
                      className={`person-avatar small ${person.sex === 'M' ? 'male' : 'female'}`}
                    >
                      {person.sex === 'M' ? '♂' : person.sex === 'F' ? '♀' : '?'}
                    </div>
                    <button
                      className="person-name-link"
                      onClick={() => handlePersonClick(person)}
                    >
                      {person.name}
                    </button>
                  </div>
                </td>
                <td className="date-cell">{person.birthDate}</td>
                <td className="date-cell">{person.deathDate}</td>
                <td className="place-cell" title={person.birthPlace}>
                  {person.birthPlace}
                </td>
                <td className="place-cell" title={person.deathPlace}>
                  {person.deathPlace}
                </td>
                <td className="actions-cell">
                  <button
                    className="action-btn tree-btn"
                    onClick={() => handleSwitchToTree(person.id)}
                    title="View in tree"
                  >
                    <i className="fas fa-sitemap"></i>
                  </button>
                  <button
                    className="action-btn details-btn"
                    onClick={() => handlePersonClick(person)}
                    title="View details"
                  >
                    <i className="fas fa-info-circle"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {sortedData.length === 0 && (
          <div className="no-results">
            <i className="fas fa-search"></i>
            <h3>No people found</h3>
            <p>Try adjusting your search terms or filters</p>
            <button 
              className="clear-filters-btn"
              onClick={clearAllFilters}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableView;